// react
import React, { useEffect, useState, useMemo } from "react";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import {
  Box,
  SwipeableDrawer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

// components
import { Puller } from "components/common/Puller";
import { LoadingDialog } from "components/loading/LoadingComponent";

// features/customer
import editCustomerPolicy from "features/customer/editCustomerPolicy";

// config
import documentTypeLabels from "../../../config/documentTypeLabels.json";

// A ---------------------------------------------------------------------- M

const getDocumentTypeLabel = (code) => {
  return documentTypeLabels[code] || code;
};

const getDisplayTipologia = (tipologia) => {
  if (tipologia === "default") return "DEFAULT ALL TYPES - ID_POLICY_67890";
  if (tipologia) return getDocumentTypeLabel(tipologia);
  return "";
};

const EditCustomerPolicyDialog = ({ client, agreement, open, setOpen }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const [tipologiaDocumentale, setTipologiaDocumentale] = useState("");
  const [condition, setCondition] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) setOpen(false);
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open, setOpen]);

  useEffect(() => {
    setTipologiaDocumentale(agreement.tipologiaDocumentale ? agreement.tipologiaDocumentale : agreement.default ? "default" : "");
    setCondition(agreement.condition);
  }, [agreement]);

  const displayTipologia = useMemo(() => getDisplayTipologia(tipologiaDocumentale), [tipologiaDocumentale]);

  const handleSave = async () => {
    setIsLoading(true);
    try {
      await editCustomerPolicy(client, tipologiaDocumentale, condition);
    } catch (error) {
      console.error("Error editing customer policy:", error);
    } finally {
      setIsLoading(false);
      setOpen(false);
    }
  };

  const FormContent = (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          required
          margin="dense"
          label={t("document_type")}
          variant="outlined"
          value={displayTipologia}
          InputProps={{ readOnly: true }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#ffffff",
            },
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControl
          fullWidth
          required
          margin="dense"
          sx={{
            mt: 1.5,
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              backgroundColor: "#ffffff",
            },
          }}
        >
          <InputLabel>{t("condition")}</InputLabel>
          <Select label={t("condition")} value={condition} onChange={(e) => setCondition(e.target.value)}>
            <MenuItem value="end_of_month">Monthly</MenuItem>
            <MenuItem value="end_of_year">Yearly</MenuItem>
            <MenuItem value="always">Daily</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </Grid>
  );

  const DialogTitleContent = () => (
    <Grid container alignItems="center" justifyContent="space-between">
      <Grid item>
        <Typography variant="h6" fontWeight={700}>
          {t("edit_policy")}
        </Typography>
      </Grid>
      <Grid item>
        <Tooltip title={t("close")} placement="top">
          <IconButton onClick={() => setOpen(false)} color="error" edge="end">
            <CloseOutlinedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    </Grid>
  );

  if (isLoading) {
    return <LoadingDialog open={isLoading} />;
  }

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "90%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
    >
      <Puller />

      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} textAlign="center" mb={3}>
          {t("edit_policy")}
        </Typography>

        {FormContent}

        <Grid container spacing={2} mt={5}>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                borderRadius: 3,
                textTransform: "none",
                fontWeight: 600,
                px: 2,
                py: 1,
              }}
              onClick={handleSave}
            >
              {t("confirm")}
            </Button>
          </Grid>
          <Grid item xs={12}>
            <Button
              fullWidth
              variant="text"
              sx={{
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={() => setOpen(false)}
            >
              {t("cancel")}
            </Button>
          </Grid>
        </Grid>
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 4,
          background: `linear-gradient(135deg, ${theme.palette.background.paper}, ${theme.palette.background.default}EE)`,
          backdropFilter: "blur(10px)",
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: "0 12px 32px rgba(0,0,0,0.4)",
          transition: "all 0.3s ease",
        },
      }}
    >
      <DialogTitle>
        <DialogTitleContent />
      </DialogTitle>

      <DialogContent>{FormContent}</DialogContent>

      <DialogActions sx={{ p: 2.5 }}>
        <Button
          variant="text"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={() => setOpen(false)}
        >
          {t("close")}
        </Button>
        <Button
          variant="contained"
          sx={{
            borderRadius: 3,
            textTransform: "none",
            fontWeight: 600,
            px: 2,
            py: 1,
          }}
          onClick={handleSave}
        >
          {t("confirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCustomerPolicyDialog;
